import { InboundCallsIcon } from '@forwardflow/assets/svg/InboundCallsIcon'
import { OutboundCallsIcon } from '@forwardflow/assets/svg/OutboundCallsIcon'
import { OverallStatsIcon } from '@forwardflow/assets/svg/OverallStatsIcon'
import { TextMessagesIcon } from '@forwardflow/assets/svg/TextMessagesIcon'
import { useGetDashboardCommunication } from '@forwardflow/hooks/customHooks'
import { AgentType, TimePeriod } from '@forwardflow/types'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const leadsOptions = [
   { label: 'All leads', value: 'allLeads' },
   { label: 'New leads', value: 'newLeads' },
   { label: 'Seller leads', value: 'sellerLeads' },
   { label: 'Buyer leads', value: 'buyerLeads' },
]

export const CommunicationStatsCard = () => {
   const [selectedLead, setSelectedLead] = useState(leadsOptions[0].value)
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const agentType = searchParams.get('agentType') || 'all'
   const {
      data: DashboardChartsLeadSource,
      isLoading: DashboardChartsLeadSourceLoading,
   } = useGetDashboardCommunication({
      timePeriod: timePeriod as TimePeriod,
      agentType: agentType as AgentType,
   })

   const statsData =
      DashboardChartsLeadSource && DashboardChartsLeadSource.conversionStats
         ? [
              {
                 type: 'Outbound Calls',
                 icon: <OutboundCallsIcon />,
                 total:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.outboundCalls?.totalCalls ?? 0,
                 answered:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.outboundCalls?.answered ?? 0,
                 converted:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.outboundCalls?.convertedToAppointment ?? 0,
                 barColor: '#4FA3FF',
                 convertedColor: '#32F3F5',
                 totalLabel: 'Total calls',
              },
              {
                 type: 'Inbound Calls',
                 icon: <InboundCallsIcon />,
                 total:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.inboundCalls?.totalCalls ?? 0,
                 answered:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.inboundCalls?.answered ?? 0,
                 converted:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.inboundCalls?.convertedToAppointment ?? 0,
                 barColor: '#4FA3FF',
                 convertedColor: '#32F3F5',
                 totalLabel: 'Total calls',
              },
              {
                 type: 'Text Messages',
                 icon: <TextMessagesIcon />,
                 total:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.textMessages?.totalMessages ?? 0,
                 answered:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.textMessages?.responded ?? 0,
                 converted:
                    DashboardChartsLeadSource.conversionStats[
                       selectedLead as keyof typeof DashboardChartsLeadSource.conversionStats
                    ]?.textMessages?.convertedToAppointment ?? 0,
                 barColor: '#FF7C4C',
                 convertedColor: '#FFD66B',
                 totalLabel: 'Messages sent',
                 answeredLabel: 'Responded',
              },
           ]
         : []

   return (
      <div className="w-full bg-white/5 rounded-xl">
         <div className="flex items-center justify-between px-6 py-4 pb-4 mb-4 border-b h-14 border-white/10">
            <div className="flex items-center gap-6 font-medium text-white">
               <OverallStatsIcon />
               Conversion Stats
            </div>
            <div className="pr-2 rounded bg-white/5">
               <select
                  className="px-3 py-2 text-white bg-transparent rounded-md focus:outline-none focus:border-transparent"
                  value={selectedLead}
                  onChange={(e) => setSelectedLead(e.target.value)}
               >
                  {leadsOptions.map((opt) => (
                     <option
                        key={opt.value}
                        value={opt.value}
                        className="bg-black/80"
                     >
                        {opt.label}
                     </option>
                  ))}
               </select>
            </div>
         </div>
         <div className="flex flex-col gap-8 p-6 pt-0 xl:flex-row">
            {DashboardChartsLeadSourceLoading ? (
               <div className="w-full py-8 text-center text-white">
                  Loading...
               </div>
            ) : (
               statsData.map((stat) => (
                  <div key={stat.type} className="flex-1">
                     <div className="flex items-center gap-3 mb-12 text-white">
                        <span className="text-xl">{stat.icon}</span>
                        <span className="font-medium">{stat.type}</span>
                        <span className="px-3 py-3 ml-auto text-xs rounded bg-white/5">
                           <span className="font-semibold">
                              {stat.total.toLocaleString()}
                           </span>
                           <span> {stat.totalLabel}</span>
                        </span>
                     </div>
                     <div className="flex justify-between mb-2 text-sm">
                        <span>{stat.answeredLabel || 'Answered'}</span>
                        <span>{stat.answered.toLocaleString()}</span>
                     </div>
                     <div className="relative w-full h-4 mb-2 bg-[#939393]/10 rounded">
                        <div
                           style={{
                              width: stat.total
                                 ? `${(stat.answered / stat.total) * 100}%`
                                 : '0%',
                              backgroundColor: stat.barColor,
                              height: '100%',
                              borderRadius: '4px',
                              transition: 'width 0.5s',
                           }}
                        />
                     </div>
                     <div className="flex justify-between mt-4 mb-2 text-sm">
                        <span>Converted to Appointment</span>
                        <span>{stat.converted.toLocaleString()}</span>
                     </div>
                     <div className="relative w-full h-4 mb-2 bg-[#939393]/10 rounded">
                        <div
                           style={{
                              width: stat.total
                                 ? `${(stat.converted / stat.total) * 100}%`
                                 : '0%',
                              backgroundColor: stat.convertedColor,
                              height: '100%',
                              borderRadius: '4px',
                              transition: 'width 0.5s',
                           }}
                        />
                     </div>
                  </div>
               ))
            )}
         </div>
      </div>
   )
}
